/**
 * OnboardingReportsTab — Subscriber's onboarding report download page
 * Route: /agent-portal/dashboard/reports (rendered inside AgentDashboardModern)
 * Backend: GET /api/reports/onboarding, GET /api/reports/onboarding/:id/download
 */

import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

interface OnboardingReport {
  id: string;
  title: string;
  status: string;
  created_at: string;
  tier: 'FULL' | 'COUNTY';
  has_file: boolean;
}

const colors = {
  navy: '#0F1E35',
  gold: '#C9952A',
  muted: '#8FA3BF',
};

const OnboardingReportsTab: React.FC = () => {
  const [reports, setReports] = useState<OnboardingReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await axiosInstance.get('/reports/onboarding');
      if (res.data.success) {
        setReports(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch onboarding reports:', err);
    }
    setLoading(false);
  };

  const handleDownload = async (reportId: string) => {
    setDownloading(reportId);
    try {
      const res = await axiosInstance.get(`/reports/onboarding/${reportId}/download`);
      if (res.data.success && res.data.downloadUrl) {
        window.open(res.data.downloadUrl, '_blank');
      } else {
        toast.error('Download link not available');
      }
    } catch (err) {
      console.error('Failed to download report:', err);
      toast.error('Failed to generate download link');
    }
    setDownloading(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spinner animation="border" size="sm" /> Loading reports...
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <Card style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12 }}>
        <Card.Body style={{ textAlign: 'center', padding: '60px 20px' }}>
          <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 48, color: colors.muted, marginBottom: 16 }} />
          <h5 style={{ color: colors.navy, marginBottom: 8 }}>No Reports Yet</h5>
          <p style={{ color: colors.muted, fontSize: 14, maxWidth: 400, margin: '0 auto' }}>
            Your AI-powered onboarding report will appear here once your book of business has been classified.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12 }}>
      <Card.Header style={{ background: colors.navy, color: '#fff', borderRadius: '12px 12px 0 0', padding: '16px 20px' }}>
        <h5 style={{ margin: 0, fontSize: 16 }}>
          <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 8, color: colors.gold }} />
          My Onboarding Reports
        </h5>
      </Card.Header>
      <Card.Body style={{ padding: 0 }}>
        <Table hover responsive style={{ margin: 0 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', fontSize: 12, textTransform: 'uppercase', color: colors.muted }}>
              <th style={{ padding: '12px 20px' }}>Report</th>
              <th style={{ padding: '12px 16px' }}>Date</th>
              <th style={{ padding: '12px 16px' }}>Tier</th>
              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {reports.map(report => (
              <tr key={report.id}>
                <td style={{ padding: '14px 20px', fontWeight: 500, color: colors.navy }}>
                  {report.title}
                </td>
                <td style={{ padding: '14px 16px', color: '#666', fontSize: 13 }}>
                  {new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <Badge bg={report.tier === 'FULL' ? 'warning' : 'secondary'}
                    style={{ fontSize: 11, padding: '4px 8px', color: report.tier === 'FULL' ? colors.navy : '#fff' }}>
                    {report.tier === 'FULL' ? 'Full (MLS)' : 'County'}
                  </Badge>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                  <Button
                    size="sm"
                    disabled={!report.has_file || downloading === report.id}
                    onClick={() => handleDownload(report.id)}
                    style={{
                      background: colors.gold, border: 'none', color: colors.navy,
                      fontWeight: 600, fontSize: 12, padding: '6px 14px',
                    }}
                  >
                    {downloading === report.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <><FontAwesomeIcon icon={faDownload} style={{ marginRight: 6 }} /> Download</>
                    )}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default OnboardingReportsTab;
